import type {
  VesselEvaluationColumn,
  VesselEvaluationFilters,
  VesselEvaluationFilterKey,
  VesselEvaluationSort,
  VesselEvaluationTableValue,
  VesselEvaluationVessel,
} from "../models/vessel-evaluation.models";

export const VESSEL_EVALUATION_COLUMNS: VesselEvaluationColumn[] = [
  { key: "vesselName", label: "Vessel Name", format: "text" },
  { key: "design", label: "Design", format: "text" },
  { key: "vesselType", label: "Vessel Type", format: "text" },
  { key: "imo", label: "IMO", format: "text" },
  { key: "status", label: "Status", format: "text" },
  { key: "deliveryDate", label: "Delivery Date", format: "date" },
  { key: "minRedeliveryDate", label: "Min Redelivery Date", format: "date" },
  { key: "maxRedeliveryDate", label: "Max Redelivery Date", format: "date" },
  { key: "contractType", label: "Contract Type", format: "text" },
  { key: "dwt", label: "DWT", format: "number" },
  { key: "buildDate", label: "Build Date", format: "month" },
  { key: "yard", label: "Yard", format: "text" },
  { key: "engineType", label: "Engine Type", format: "text" },
  { key: "mcr", label: "MCR", format: "number" },
  { key: "eplMcrLimit", label: "EPL MCR Limit", format: "number" },
  { key: "scrubber", label: "Scrubber", format: "boolean" },
  { key: "esd", label: "ESD", format: "text" },
  { key: "ladenDraftCorrection", label: "Laden Draft Correction", format: "number" },
  { key: "ballastDraftCorrection", label: "Ballast Draft Correction", format: "number" },
  { key: "refAe", label: "Ref AE", format: "number" },
  { key: "cleanHullSpeedBallastEco", label: "Clean Hull Speed Ballast ECO", format: "number" },
  { key: "consBallastEco", label: "Cons Ballast ECO", format: "number" },
  { key: "currentSpeedBallastEco", label: "Current Speed Ballast ECO", format: "number" },
  { key: "cleanHullSpeedBallastFull", label: "Clean Hull Speed Ballast FULL", format: "number" },
  { key: "consBallastFull", label: "Cons Ballast FULL", format: "number" },
  { key: "currentSpeedBallastFull", label: "Current Speed Ballast FULL", format: "number" },
  { key: "cleanHullSpeedLadenEco", label: "Clean Hull Speed Laden ECO", format: "number" },
  { key: "consLadenEco", label: "Cons Laden ECO", format: "number" },
  { key: "currentSpeedLadenEco", label: "Current Speed Laden ECO", format: "number" },
  { key: "cleanHullSpeedLadenFull", label: "Clean Hull Speed Laden FULL", format: "number" },
  { key: "consLadenFull", label: "Cons Laden FULL", format: "number" },
  { key: "currentSpeedLadenFull", label: "Current Speed Laden FULL", format: "number" },
  { key: "comment", label: "Comment", format: "text" },
  { key: "currentAt115Knt", label: "Current at 11.5 knt", format: "number" },
  { key: "cpAt115Knt", label: "CP at 11.5 knt", format: "number" },
  { key: "cleanAt115Knt", label: "Clean at 11.5 knt", format: "number" },
  { key: "currentAt125Knt", label: "Current at 12.5 knt", format: "number" },
  { key: "cpAt125Knt", label: "CP at 12.5 knt", format: "number" },
  { key: "cleanAt125Knt", label: "Clean at 12.5 knt", format: "number" },
  { key: "evaluatedVps", label: "Evaluated VPS", format: "date" },
  { key: "cpBallastEcoSpeedCons", label: "CP Ballast ECO Speed / Cons", format: "text" },
  { key: "cpBallastFullSpeedCons", label: "CP Ballast FULL Speed / Cons", format: "text" },
  { key: "cpLadenEcoSpeedCons", label: "CP Laden ECO Speed / Cons", format: "text" },
  { key: "cpLadenFullSpeedCons", label: "CP Laden FULL Speed / Cons", format: "text" },
  { key: "paintVendor", label: "Paint Vendor", format: "text" },
  { key: "lastDryDock", label: "Last Dry Dock", format: "date" },
  { key: "antifoulingAppliedDate", label: "A/F Applied Date", format: "date" },
  { key: "fb", label: "FB", format: "text" },
  { key: "vs", label: "VS", format: "text" },
  { key: "bt", label: "BT", format: "text" },
  { key: "propellerCoating", label: "Propeller Coating", format: "text" },
  { key: "paintSchemeFor", label: "Paint Scheme For", format: "text" },
  { key: "dryDockComment", label: "Dry Dock Comment", format: "text" },
  { key: "latestHullEvent", label: "Latest Hull Event", format: "text" },
  { key: "hullCleaned", label: "Hull Cleaned", format: "text" },
  { key: "propellerPolished", label: "Propeller Polished", format: "text" },
  { key: "placeVendor", label: "Place / Vendor", format: "text" },
  { key: "minMcrPercent", label: "Min MCR %", format: "number" },
  { key: "maxMcrPercent", label: "Max MCR %", format: "number" },
];

const BLANK_FILTER_VALUE = "(Blank)";

const getFilterValue = (value: string | null): string =>
  value?.trim() ? value : BLANK_FILTER_VALUE;
const getBuildYear = (vessel: VesselEvaluationVessel): string => getFilterValue(vessel.buildDate?.slice(0, 4) ?? null);
const getScrubberLabel = (vessel: VesselEvaluationVessel): string =>
  vessel.scrubber == null ? BLANK_FILTER_VALUE : String(vessel.scrubber);

const FILTER_VALUE_READERS: Record<VesselEvaluationFilterKey, (vessel: VesselEvaluationVessel) => string> = {
  imo: (vessel) => vessel.imo,
  status: (vessel) => getFilterValue(vessel.status),
  vesselType: (vessel) => getFilterValue(vessel.vesselType),
  buildYear: getBuildYear,
  paintVendor: (vessel) => getFilterValue(vessel.paintVendor),
  vesselName: (vessel) => vessel.vesselName,
  contractType: (vessel) => getFilterValue(vessel.contractType),
  design: (vessel) => getFilterValue(vessel.design),
  engineType: (vessel) => getFilterValue(vessel.engineType),
  scrubber: getScrubberLabel,
};

// Applies every selected slicer as an AND while values inside one slicer remain an OR, matching Power BI.
export const filterVesselEvaluations = (
  vessels: VesselEvaluationVessel[],
  filters: VesselEvaluationFilters,
): VesselEvaluationVessel[] =>
  vessels.filter((vessel) =>
    (Object.keys(filters) as VesselEvaluationFilterKey[]).every((key) =>
      filters[key].length === 0 || filters[key].includes(FILTER_VALUE_READERS[key](vessel)),
    ),
  );

export const getVesselEvaluationFilterOptions = (
  vessels: VesselEvaluationVessel[],
  filters: VesselEvaluationFilters,
  key: VesselEvaluationFilterKey,
): string[] => {
  const filtersWithoutCurrentSlicer = { ...filters, [key]: [] };
  const crossFilteredVessels = filterVesselEvaluations(vessels, filtersWithoutCurrentSlicer);

  return Array.from(new Set(crossFilteredVessels.map(FILTER_VALUE_READERS[key]))).sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true }),
  );
};

const comparableValue = (value: VesselEvaluationTableValue): string | number => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "boolean") {
    return Number(value);
  }

  return value?.toLowerCase() ?? "";
};

const VESSEL_EVALUATION_SUM_FIELDS = new Set<keyof VesselEvaluationVessel>([
  "ladenDraftCorrection",
  "ballastDraftCorrection",
  "refAe",
  "cleanHullSpeedBallastEco",
  "consBallastEco",
  "currentSpeedBallastEco",
  "cleanHullSpeedBallastFull",
  "consBallastFull",
  "cleanHullSpeedLadenEco",
  "consLadenEco",
  "currentSpeedLadenEco",
  "cleanHullSpeedLadenFull",
  "consLadenFull",
  "currentSpeedLadenFull",
  "currentAt115Knt",
  "cpAt115Knt",
  "cleanAt115Knt",
  "minMcrPercent",
  "maxMcrPercent",
]);

const VESSEL_EVALUATION_AVERAGE_FIELDS = new Set<keyof VesselEvaluationVessel>([
  "currentSpeedBallastFull",
  "currentAt125Knt",
  "cpAt125Knt",
  "cleanAt125Knt",
]);

const compareVesselEvaluationValues = (
  left: VesselEvaluationTableValue,
  right: VesselEvaluationTableValue,
): number => {
  const leftValue = comparableValue(left);
  const rightValue = comparableValue(right);

  return typeof leftValue === "number" && typeof rightValue === "number"
    ? leftValue - rightValue
    : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true });
};

const aggregateVesselEvaluationMinimum = (
  values: VesselEvaluationTableValue[],
): VesselEvaluationTableValue => {
  const populatedValues = values.filter((value) => value != null);

  if (populatedValues.length === 0) {
    return null;
  }

  return populatedValues.reduce((minimum, value) =>
    compareVesselEvaluationValues(value, minimum) < 0 ? value : minimum,
  );
};

const aggregateVesselEvaluationNumber = (
  values: VesselEvaluationTableValue[],
  useAverage: boolean,
): number | null => {
  const populatedValues = values.filter((value): value is number => typeof value === "number");

  if (populatedValues.length === 0) {
    return null;
  }

  const total = populatedValues.reduce((sum, value) => sum + value, 0);

  return useAverage ? total / populatedValues.length : total;
};

const aggregateVesselEvaluationField = (
  vessels: VesselEvaluationVessel[],
  key: keyof VesselEvaluationVessel,
): VesselEvaluationTableValue => {
  const values = vessels.map((vessel) => vessel[key]);

  if (VESSEL_EVALUATION_SUM_FIELDS.has(key)) {
    return aggregateVesselEvaluationNumber(values, false);
  }

  if (VESSEL_EVALUATION_AVERAGE_FIELDS.has(key)) {
    return aggregateVesselEvaluationNumber(values, true);
  }

  return aggregateVesselEvaluationMinimum(values);
};

// Groups the filtered source rows using the matrix's recovered Power BI aggregation metadata.
export const groupVesselEvaluationsByVesselName = (
  vessels: VesselEvaluationVessel[],
): VesselEvaluationVessel[] => {
  const vesselsByName = new Map<string, VesselEvaluationVessel[]>();

  vessels.forEach((vessel) => {
    const groupedVessels = vesselsByName.get(vessel.vesselName) ?? [];
    groupedVessels.push(vessel);
    vesselsByName.set(vessel.vesselName, groupedVessels);
  });

  return Array.from(vesselsByName.values()).map((groupedVessels) => {
    const aggregatedVessel = { ...groupedVessels[0] };

    VESSEL_EVALUATION_COLUMNS.forEach(({ key }) => {
      Object.assign(aggregatedVessel, {
        [key]: aggregateVesselEvaluationField(groupedVessels, key),
      });
    });

    return aggregatedVessel;
  });
};

export const sortVesselEvaluations = (
  vessels: VesselEvaluationVessel[],
  sort: VesselEvaluationSort,
): VesselEvaluationVessel[] =>
  [...vessels].sort((left, right) => {
    const comparison = compareVesselEvaluationValues(left[sort.key], right[sort.key]);

    return sort.direction === "ascending" ? comparison : -comparison;
  });

export const formatVesselEvaluationValue = (
  value: VesselEvaluationTableValue,
  format: VesselEvaluationColumn["format"],
): string => {
  if (value == null || value === "") {
    return "";
  }

  if (format === "boolean") {
    return value ? "true" : "false";
  }

  if (format === "number" && typeof value === "number") {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
  }

  if (format === "date" && typeof value === "string") {
    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          timeZone: "UTC",
          year: "2-digit",
        }).format(date).replace(/ /g, "-");
  }

  if (format === "month" && typeof value === "string") {
    return value.slice(0, 7);
  }

  return String(value);
};

export const formatVesselEvaluationSnapshotTime = (value: string | null): string => {
  if (!value) {
    return "Not loaded";
  }

  const snapshotTime = new Date(value);

  return Number.isNaN(snapshotTime.getTime()) ? "Unavailable" : snapshotTime.toLocaleString();
};
